/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { CostCenterFinderListItem } from '@gh/builder-shared';
import {
  ContactListItemDto,
  JcsDetailsCcDto,
  JcsDetailsDto,
  JcsDetailsHeadingBase,
  JcsDetailsHeadingDto,
  JcsDetailsItemBase,
  JcsDetailsItemDto,
  JcsDetailsSubheadingBase,
  JcsDetailsSubheadingDto,
} from '@gh/builder-shared-data';
import {
  createArrayHelper,
  DateTimeTypes,
  EntityConfig,
  entityStateMerger,
  entityValidatorByEntityConfig,
  errorsEntityState,
  fieldStateByEntityConfig,
  fieldValidatorByEntityConfig,
  fieldValidatorFrom,
  nestedArrayValidator,
  NumberTypes,
  setEntityFields,
  TextTypes,
} from '@gh/common-shared';

import { AutocompleteItem, ID, WithDirty, WithEntityState, WithVersion } from '@gh/common-shared-data';
import { validationError } from '@gh/core-ui';
import { cast, isNotNil, toString, valueComparator } from '@gh/core-util';
import { find, isEmpty, isNil, omit, pick, sortBy, uniqueId, without } from 'lodash';

export enum JobCsDetailsLevel { Heading = 0, Subheading = 1, Item = 2, CostCenter = 3 }

export type JobCsInit = { master: AutocompleteItem };

export type JobCsConsultant = { contactId: ID; name: string };

export type JobCsDetailsMasterInfo = {
  jobNumber: string;
  customerName: string;
  contactId: ID;
  siteAddress: string;
  colorConsultant: JobCsConsultant;
  csSignedByClientDate?: Date;
  csSignedByClient: boolean;
  csSignedByOfficeDate?: Date;
  csSignedByOffice: boolean;
  csDocSigningId?: ID;
  isSignedManually: boolean;
  produced: boolean;
};

export type JobCsDetailsHeading = WithDirty<WithEntityState<JcsDetailsHeadingBase & {
  id: ID;
  isNew?: true;
  subHeadings: JobCsDetailsSubheading[];
}>>;
export type JobCsDetailsSubheading = WithDirty<WithEntityState<JcsDetailsSubheadingBase & {
  id: ID;
  isNew?: true;
  items: JobCsDetailsItem[];
}>>;
export type JobCsDetailsItem = WithDirty<WithEntityState<JcsDetailsItemBase & {
  id: ID;
  isNew?: true;
  costCenters: JobCsDetailsCc[];
  qcName?: string;
  isQuoteChange: boolean;
}>>;
// we have to use generated ID for cost center list, because costCenterId can be modified by user
export type JobCsDetailsCc = WithDirty<WithEntityState<JcsDetailsCcDto & {
  id: ID;
  costCenter?: AutocompleteItem;
  isNew?: true;
}>>;

export type JobCsDetails = WithVersion<{
  jobId: ID;
  csMasterId: ID;
  name: string;
  jobNumber: string;
  customerName: string;
  siteAddress: string;
  hadEmptyItems: boolean;
  isNotProceeding: boolean;
  isFinalized: boolean;
  colorConsultant?: JobCsConsultant;
  csDocSigningId?: ID;
  isSignedManually: boolean;
  csSignedByClientDate?: Date;
  csSignedByClientName?: Date;
  csSignedByOfficeDate?: Date;
  csSignedByOfficeName?: Date;
  headings: JobCsDetailsHeading[];
  quoteChanges?: JobCsDetailsHeading;
  isJobCsCostCenterClone: boolean;
  isJobCsCostCenterRequired: boolean;
}>;

const hasHeadingEmptyItem = (heading: JobCsDetailsHeading) =>
  heading.subHeadings
    .some((subHeading) => subHeading.items
      .some((item) => isEmpty(item.costCenters)));

export const jobCsDetailsCcFromFinder = (cc: CostCenterFinderListItem): JobCsDetailsCc => ({
  id: jobCsCcGenerateId(),
  isDirty: true,
  isNew: true,
  costCenterId: cc.id,
  code: cc.code,
  description: cc.name,
  costCenter: {
    id: cc.id,
    name: cc.code.toString(),
    display: cc.code.toString(),
  },
});

const jobCsDetailsCcFromDto = (cc: JcsDetailsCcDto): JobCsDetailsCc => ({
  id: jobCsCcGenerateId(),
  ...cc,
  costCenter: {
    id: cc.costCenterId,
    name: toString(cc.code),
    display: toString(cc.code),
  },
});
const jobCsDetailsItemFromDto = (item: JcsDetailsItemDto,
                                 isQuoteChange: boolean): JobCsDetailsItem => complementJobCsDetailsItem({
  id: jobCsItemGenerateId(),
  ...item,
  isQuoteChange,
  qcName: isQuoteChange ? item.name : void 0,
  costCenters: (item.costCenters || []).map(jobCsDetailsCcFromDto),
});
const jobCsDetailsSubheadingFromDto = (subheading: JcsDetailsSubheadingDto): JobCsDetailsSubheading => {
  const items = (subheading.items || []).map((item) => jobCsDetailsItemFromDto(item, subheading.isOqc));
  return complementJobCsDetailsSubheading({
    id: jobCsSubheadingGenerateId(),
    ...subheading,
    // quote changes should be sorted according to the name
    items: subheading.isOqc ? sortBy(items, 'qcName') : items,
  });
};
const jobCsDetailsHeadingFromDto = (heading: JcsDetailsHeadingDto): JobCsDetailsHeading =>
  complementJobCsDetailsHeading({
    // @ts-ignore
    id: jobCsHeadingGenerateId(),
    ...cast<JobCsDetailsHeading>(omit(heading, 'subHeadings')),
    subHeadings: (heading.subHeadings || []).map(jobCsDetailsSubheadingFromDto),
  });
export const jobCsDetailsFromDto = (jobId: ID, details: JcsDetailsDto): JobCsDetails => {
  const headings = (details.headings || []).map(jobCsDetailsHeadingFromDto);
  const quoteChanges = find(headings, { isOqc: true });

  return {
    // @ts-ignore
    jobId,
    ...cast<JobCsDetails>(omit(details, ['colorConsultantId', 'colorConsultantName', 'headings'])),
    hadEmptyItems: headings.some(hasHeadingEmptyItem),
    colorConsultant: details.colorConsultantId
      ? { contactId: details.colorConsultantId, name: details.colorConsultantName } : void 0,
    headings: quoteChanges ? without(headings, quoteChanges) : headings,
    quoteChanges,
  };
};

const jobCsDetailsCcToDto = (cc: JobCsDetailsCc) => <JcsDetailsCcDto>
  omit(cc, ['id', 'isDirty', 'isNew', 'costCenter', 'entityState']);
const jobCsDetailsItemToDto = (item: JobCsDetailsItem) => <JcsDetailsItemDto>({
  ...omit(item, ['id', 'isDirty', 'isNew', 'entityState', 'qcName', 'isQuoteChange']),
  csItemId: item.isNew ? void 0 : item.csItemId,
  costCenters: item.costCenters.map(jobCsDetailsCcToDto),
});
const jobCsDetailsSubheadingToDto = (subheading: JobCsDetailsSubheading) => <JcsDetailsSubheadingDto>({
  ...omit(subheading, ['id', 'isDirty', 'isNew', 'entityState']),
  csSubHeadingId: subheading.isNew ? void 0 : subheading.csSubHeadingId,
  items: subheading.items.map(jobCsDetailsItemToDto),
});
const jobCsDetailsHeadingToDto = (heading: JobCsDetailsHeading) => <JcsDetailsHeadingDto>({
  ...omit(heading, ['id', 'isDirty', 'isNew', 'entityState']),
  csHeadingId: heading.isNew ? void 0 : heading.csHeadingId,
  subHeadings: heading.subHeadings.map(jobCsDetailsSubheadingToDto),
});
export const jobCsDetailsToDto = (model: JobCsDetails) => <JcsDetailsDto>({
  ...cast<JcsDetailsDto>(omit(model, ['colorConsultant', 'jobId', 'headings', 'quoteChanges'])),
  colorConsultantId: model.colorConsultant && model.colorConsultant.contactId,
  colorConsultantName: model.colorConsultant && model.colorConsultant.name,
  headings: model.headings.concat(model.quoteChanges ? [model.quoteChanges] : []).map(jobCsDetailsHeadingToDto),
});

export const jobCsDetailsMasterInfoFromModel = (details: JobCsDetails): JobCsDetailsMasterInfo => ({
  ...cast<JobCsDetailsMasterInfo>(pick(details, [
    'jobNumber', 'customerName', 'siteAddress', 'colorConsultant', 'csDocSigningId', 'isSignedManually', 'produced',
    'csSignedByClientDate', 'csSignedByClientName', 'csSignedByOfficeDate', 'csSignedByOfficeName', 'contactId',
  ])),
  csSignedByClient: isNotNil(details.csSignedByClientDate),
  csSignedByOffice: isNotNil(details.csSignedByOfficeDate),
});

export const jobCsDetailsCcClone = (cc: JobCsDetailsCc): JobCsDetailsCc => ({
  ...cc,
  id: jobCsCcGenerateId(),
  isNew: true,
  isDirty: true,
});

export const isJobCsItemQuoteChange = (item: JobCsDetailsItem) => item.isQuoteChange;

export const jobCsDetailsCcComparator = valueComparator('code');
export const getJobCsDetailsHeadingId = (heading: JobCsDetailsHeading) => heading.id;
export const getJobCsDetailsSubheadingId = (heading: JobCsDetailsSubheading) => heading.id;
export const getJobCsDetailsItemId = (item: JobCsDetailsItem) => item.id;
export const getJobCsDetailsCcId = (cc: JobCsDetailsCc) => cc.id;

export const JOB_CS_DETAILS_MASTER_INFO_ENTITY_CONFIG: EntityConfig<JobCsDetailsMasterInfo> = {
  colorConsultant: { required: true },
  csSignedByClientDate: {
    required: ({ csSignedByClient }) => csSignedByClient,
    type: DateTimeTypes.ShortDate,
    validator: ({ csSignedByOfficeDate }, _, csSignedByClientDate) =>
      isNil(csSignedByClientDate) || isNil(csSignedByOfficeDate) || csSignedByClientDate <= csSignedByOfficeDate
        ? void 0
        : { dateRange: validationError('msg_field_invalid_format') },
  },
  csSignedByOfficeDate: {
    required: ({ csSignedByOffice }) => csSignedByOffice,
    type: DateTimeTypes.ShortDate,
    validator: ({ csSignedByClientDate }, _, csSignedByOfficeDate) =>
      isNil(csSignedByClientDate) || isNil(csSignedByOfficeDate) || csSignedByClientDate <= csSignedByOfficeDate
        ? void 0
        : { dateRange: validationError('msg_field_invalid_format') },
  },
};
export const validateJobCsDetailsMasterInfo = entityValidatorByEntityConfig(JOB_CS_DETAILS_MASTER_INFO_ENTITY_CONFIG);

export const JOB_CS_DETAILS_HEADING_ENTITY_CONFIG: EntityConfig<JobCsDetailsHeading> = {
  name: { required: true, type: TextTypes.Text40 },
  subHeadings: { validator: fieldValidatorFrom(nestedArrayValidator) },
};
export const jobCsDetailsHeadingFieldState = fieldStateByEntityConfig(JOB_CS_DETAILS_HEADING_ENTITY_CONFIG);
export const validateJobCsDetailsHeadingField = fieldValidatorByEntityConfig(JOB_CS_DETAILS_HEADING_ENTITY_CONFIG);
export const validateJobCsDetailsHeading = entityValidatorByEntityConfig(JOB_CS_DETAILS_HEADING_ENTITY_CONFIG);
export const complementJobCsDetailsHeading = entityStateMerger(errorsEntityState(validateJobCsDetailsHeading));

export const JOB_CS_DETAILS_SUB_HEADING_ENTITY_CONFIG: EntityConfig<JobCsDetailsSubheading> = {
  name: { required: true, type: TextTypes.Text40 },
  items: { validator: fieldValidatorFrom(nestedArrayValidator) },
};
export const jobCsDetailsSubheadingFieldState = fieldStateByEntityConfig(JOB_CS_DETAILS_SUB_HEADING_ENTITY_CONFIG);
export const validateJobCsDetailsSubheadingField =
  fieldValidatorByEntityConfig(JOB_CS_DETAILS_SUB_HEADING_ENTITY_CONFIG);
export const validateJobCsDetailsSubheading = entityValidatorByEntityConfig(JOB_CS_DETAILS_SUB_HEADING_ENTITY_CONFIG);
export const complementJobCsDetailsSubheading = entityStateMerger(errorsEntityState(validateJobCsDetailsSubheading));

export const JOB_CS_DETAILS_ITEM_ENTITY_CONFIG: EntityConfig<JobCsDetailsItem> = {
  name: { required: true, type: TextTypes.Text40 },
  description: { required: false, type: TextTypes.Text255 },
  variationNumber: { editable: (item) => item.isVariationRequired, type: NumberTypes.VariationNumber },
  costCenters: { validator: fieldValidatorFrom(nestedArrayValidator) },
};
export const jobCsDetailsItemFieldState = fieldStateByEntityConfig(JOB_CS_DETAILS_ITEM_ENTITY_CONFIG);
export const validateJobCsDetailsItemField = fieldValidatorByEntityConfig(JOB_CS_DETAILS_ITEM_ENTITY_CONFIG);
export const validateJobCsDetailsItem = entityValidatorByEntityConfig(JOB_CS_DETAILS_ITEM_ENTITY_CONFIG);
export const complementJobCsDetailsItem = entityStateMerger(errorsEntityState(validateJobCsDetailsItem));

export const JOB_CS_DETAILS_CC_ENTITY_CONFIG: EntityConfig<JobCsDetailsCc> = {
  costCenter: { required: true },
};
export const jobCsDetailsCcFieldState = fieldStateByEntityConfig(JOB_CS_DETAILS_CC_ENTITY_CONFIG);
export const validateJobCsDetailsCcField = fieldValidatorByEntityConfig(JOB_CS_DETAILS_CC_ENTITY_CONFIG);
export const validateJobCsDetailsCc = entityValidatorByEntityConfig(JOB_CS_DETAILS_CC_ENTITY_CONFIG);
export const complementJobCsDetailsCc = entityStateMerger(errorsEntityState(validateJobCsDetailsCc));

export const jobCsConsultantFromContactListItemDto = ({ id, name }: ContactListItemDto) => ({ contactId: id, name });

export const jobCsHeadingGenerateId = () => uniqueId('job-cs-heading:');
export const jobCsSubheadingGenerateId = () => uniqueId('job-cs-subheading:');
export const jobCsItemGenerateId = () => uniqueId('job-cs-item:');
export const jobCsCcGenerateId = () => uniqueId('job-cs-cc:');

export const jobCsCcClone = (item: JobCsDetailsCc): JobCsDetailsCc => ({
  ...item,
  id: jobCsCcGenerateId(),
  isNew: true,
});

export const jobCsItemClone = (item: JobCsDetailsItem): JobCsDetailsItem => ({
  ...item,
  id: jobCsItemGenerateId(),
  isNew: true,
  costCenters: item.costCenters.map(jobCsCcClone),
});

export const jobCsSubheadingClone = (subheading: JobCsDetailsSubheading): JobCsDetailsSubheading => ({
  ...subheading,
  id: jobCsSubheadingGenerateId(),
  isNew: true,
  isHidden: false,
  items: subheading.items.map(jobCsItemClone),
});

export const jobCsHeadingClone = (heading: JobCsDetailsHeading): JobCsDetailsHeading => ({
  ...heading,
  id: jobCsHeadingGenerateId(),
  isNew: true,
  subHeadings: heading.subHeadings.map(jobCsSubheadingClone),
});

export const jobCsHeadingArr = createArrayHelper(getJobCsDetailsHeadingId);
export const jobCsSubheadingArr = createArrayHelper(getJobCsDetailsSubheadingId);
export const jobCsItemArr = createArrayHelper(getJobCsDetailsItemId);
export const jobCsCostCenterArr = createArrayHelper(getJobCsDetailsCcId);

export const mergeHeadings = (source: JobCsDetailsHeading[], updated: JobCsDetailsHeading[]) =>
  jobCsHeadingArr.orderedMergeWith(source, updated, mergeHeading);

const mergeHeading = (source: JobCsDetailsHeading, updated: JobCsDetailsHeading) => setEntityFields(source, {
  isNew: false,
  isDirty: false,
  csHeadingId: updated.csHeadingId,
  subHeadings: jobCsSubheadingArr.orderedMergeWith(source.subHeadings, updated.subHeadings, mergeSubheading),
});

const mergeSubheading = (source: JobCsDetailsSubheading, updated: JobCsDetailsSubheading) => setEntityFields(source, {
  isNew: false,
  isDirty: false,
  csSubHeadingId: updated.csSubHeadingId,
  items: jobCsItemArr.orderedMergeWith(source.items, updated.items, mergeItem),
});

const mergeItem = (source: JobCsDetailsItem, updated: JobCsDetailsItem) => setEntityFields(source, {
  isNew: false,
  isDirty: false,
  isQuoteChange: false,
  csItemId: updated.csItemId,
  costCenters: jobCsCostCenterArr.orderedMergeWith(source.costCenters, updated.costCenters, mergeCostCenter),
});

const mergeCostCenter = (source: JobCsDetailsCc) => setEntityFields(source, {
  isNew: false,
  isDirty: false,
});
